/**
 * Named helpers for the Finance / Invoices print + export endpoints.
 *
 * Design rules:
 *   • One helper per endpoint — the previous single "openSecuredPrint(path)"
 *     let callers slip strings like "/invoice/list/print" or
 *     "/invoice/{id}/print" through the same function. If a call site
 *     forgot to interpolate the id you'd end up hitting the wrong route.
 *     Named helpers make wrong usage a type error.
 *   • Never emit `undefined`, `null`, `""`, `NaN`, or the literal string
 *     "undefined" into the URL — `buildQuery()` strips them.
 *   • On failure, surface a proper application notification via the
 *     project's Notifier (through a small CustomEvent bridge so this
 *     module doesn't have to import React hooks). Never `window.alert`.
 *
 * Auth: browsers can't attach a Bearer header to <a href> or window.open,
 * so we fetch through the authorized fetch and open the response as a
 * Blob URL — works for text/html and application/pdf identically.
 */
import { API_URL } from "../../../config";

// ── Notifier bridge ─────────────────────────────────────────────────
// The Invoices page listens for "finance:notify" and forwards to the
// project's useNotifier(). Keeps this file free of React hooks so it
// stays usable from anywhere (helpers, workers, tests).
type NotifyLevel = "error" | "success" | "info";
function notify(level: NotifyLevel, message: string): void {
  try {
    window.dispatchEvent(new CustomEvent("finance:notify", { detail: { level, message } }));
  } catch {
    // Fall back to console when the environment can't dispatch events (SSR / tests).
    // eslint-disable-next-line no-console
    console.warn(`[finance:${level}]`, message);
  }
}

// Build a querystring that skips empty / null / undefined / "undefined" /
// NaN. Order-independent; boolean true → "1".
export function buildQuery(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "" || v === "undefined") return;
    if (typeof v === "number" && !Number.isFinite(v)) return;
    if (typeof v === "boolean") { if (v) p.set(k, "1"); return; }
    p.set(k, String(v));
  });
  return p.toString();
}

// Guard: throw the same message the notifier will display when the id
// argument is not a positive integer. Prevents "/invoice/undefined/print".
function assertNumericId(kind: string, id: unknown): number {
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) {
    throw new Error(`Cannot open ${kind} — invalid identifier.`);
  }
  return n;
}

// Fetch + open through Blob URL. Kept private so callers use the named
// helpers below. Uses a per-endpoint friendly failure message so the
// user sees "Unable to open the invoice report" instead of raw HTTP text.
async function openSecured(
  path: string,
  filename: string | undefined,
  friendlyFailure: string
): Promise<void> {
  const url = String(API_URL || "").replace(/\/$/, "") + path;
  const token = localStorage.getItem("token") || "";
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      // Try to extract a helpful backend message but never dump raw HTML
      // at the user.
      let detail = `HTTP ${res.status}`;
      try {
        const text = await res.text();
        try {
          const j = JSON.parse(text);
          if (j?.message) detail = String(j.message);
        } catch {
          // non-JSON — ignore body
        }
      } catch { /* swallow */ }
      throw new Error(detail);
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const win = window.open(blobUrl, "_blank");
    if (!win) {
      // Popup blocked — degrade to a direct download.
      const a = document.createElement("a");
      a.href = blobUrl;
      if (filename) a.download = filename;
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err: any) {
    notify("error", `${friendlyFailure}${err?.message ? ` (${err.message})` : ""}`);
  }
}

// ── Named endpoint helpers ──────────────────────────────────────────

/** GET /invoice/list/print — printable filtered report. */
export function openInvoiceListPrint(filters: Record<string, unknown>): Promise<void> {
  const qs = buildQuery(filters);
  return openSecured(
    `/invoice/list/print${qs ? `?${qs}` : ""}`,
    `invoice-report.html`,
    "Unable to open the invoice report. Please try again."
  );
}

/** GET /invoice/list/export — streamed CSV of all filtered rows. */
export function openInvoiceListExport(filters: Record<string, unknown>): Promise<void> {
  const qs = buildQuery(filters);
  return openSecured(
    `/invoice/list/export${qs ? `?${qs}` : ""}`,
    `invoices.csv`,
    "Unable to export invoices. Please try again."
  );
}

/**
 * @deprecated Do not use for user-facing Print buttons. The full-invoice
 * Print action must go through {@link openInvoicePdf} so the browser opens
 * the real Dompdf-rendered PDF (same design as the Print List) instead of
 * a raw HTML Blob. Kept exported only for the debug HTML preview at
 * `/invoice/{id}/print?format=html`.
 */
export function openInvoicePrint(invoiceId: number | string, invoiceNumber?: string): Promise<void> {
  const id = assertNumericId("invoice", invoiceId);
  return openSecured(
    `/invoice/${id}/print?format=html`,
    `Invoice-${invoiceNumber || id}.html`,
    "Unable to open the invoice. Please try again."
  );
}

/** GET /invoice/{id}/pdf — real PDF (application/pdf). */
export function openInvoicePdf(invoiceId: number | string, invoiceNumber?: string, mode?: "download"): Promise<void> {
  const id = assertNumericId("invoice", invoiceId);
  const suffix = mode === "download" ? "?mode=download" : "";
  return openSecured(
    `/invoice/${id}/pdf${suffix}`,
    `Invoice-${invoiceNumber || id}.pdf`,
    "Unable to open the invoice PDF. Please try again."
  );
}

/** GET /receipt/{payment}/print — printable receipt. */
export function openReceiptPrint(paymentId: number | string, receiptNumber?: string): Promise<void> {
  const id = assertNumericId("receipt", paymentId);
  return openSecured(
    `/receipt/${id}/print`,
    `Receipt-${receiptNumber || id}.html`,
    "Unable to open the receipt. Please try again."
  );
}

/** GET /receipt/{payment}/pdf — real PDF receipt. */
export function openReceiptPdf(paymentId: number | string, receiptNumber?: string, mode?: "download"): Promise<void> {
  const id = assertNumericId("receipt", paymentId);
  const suffix = mode === "download" ? "?mode=download" : "";
  return openSecured(
    `/receipt/${id}/pdf${suffix}`,
    `Receipt-${receiptNumber || id}.pdf`,
    "Unable to open the receipt PDF. Please try again."
  );
}
